import { useState, type ChangeEvent } from "react";
import { api } from "../lib/api";
import { useAuth } from "../app/AuthContext";

const TEMPLATE = `name,website,instagram,country,category,notes
Example Brand,https://examplebrand.com,@examplebrand,Denmark,Outerwear,Found via stockist`;

export function AdminImportPage() {
  const { user } = useAuth();
  const [text, setText] = useState(TEMPLATE);
  const [result, setResult] = useState<Awaited<ReturnType<typeof api.admin.importBrands>> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (!user || (user.role !== "admin" && user.role !== "super_admin")) {
    return <div className="container empty-state">Admin access required.</div>;
  }

  const onFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    file.text().then(setText);
  };

  const onImport = async () => {
    setError(null); setResult(null); setPending(true);
    try {
      const rows = parseCsv(text);
      if (rows.length === 0) throw new Error("No rows parsed");
      const r = await api.admin.importBrands(rows);
      setResult(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="container" style={{ paddingBlock: "2rem", maxWidth: 920 }}>
      <h2>Import brands (CSV)</h2>
      <p style={{ color: "var(--fg-muted)", marginTop: "1rem" }}>
        Columns: <code>name</code>, <code>website</code>, <code>instagram</code>, <code>country</code>, <code>category</code>, <code>notes</code>.
        Each row creates a brand and a candidate, which gets queued through the extraction pipeline.
      </p>

      <input type="file" accept=".csv,text/csv" onChange={onFile} style={{ marginTop: "1.5rem" }} />
      <textarea
        rows={16}
        value={text}
        onChange={(e) => setText(e.target.value)}
        style={{ marginTop: "1rem", fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: "0.85rem" }}
      />
      <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem" }}>
        <button className="primary" onClick={onImport} disabled={pending}>{pending ? "Importing…" : "Import"}</button>
      </div>

      {error && <div style={{ color: "var(--danger)", marginTop: "1rem" }}>{error}</div>}
      {result && (
        <div style={{ marginTop: "1.5rem" }}>
          <p style={{ color: "var(--ok)" }}>Created {result.created} · Skipped {result.skipped}</p>
          {result.skippedDetails.length > 0 && (
            <details>
              <summary>Skipped rows</summary>
              <pre style={{ background: "var(--bg-elevated)", padding: "1rem", borderRadius: "var(--radius)", overflow: "auto", fontSize: "0.8rem" }}>
                {JSON.stringify(result.skippedDetails, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function parseCsv(text: string): Array<Record<string, string>> {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return [];
  const headers = splitCsvLine(lines[0]);
  return lines.slice(1).map((line) => {
    const cells = splitCsvLine(line);
    const row: Record<string, string> = {};
    headers.forEach((h, i) => { row[h] = (cells[i] ?? "").trim(); });
    return row;
  });
}

// Minimal CSV splitter that handles double-quoted fields with embedded commas.
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = ""; let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') { inQuotes = false; }
      else { cur += ch; }
    } else {
      if (ch === ",") { out.push(cur); cur = ""; }
      else if (ch === '"') { inQuotes = true; }
      else { cur += ch; }
    }
  }
  out.push(cur);
  return out;
}
